import { Check, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/catalyst/button';
import { Checkbox, CheckboxField } from '@/components/ui/catalyst/checkbox';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import {
  ProviderRequestPlanRoutingOptionLocation,
  ProviderRequestPlanRoutingOptionService,
  ProviderRequestPlanSchema,
  ProviderRequestPlanScopeSchema,
  TreatmentRecordTypes,
  useProviderLocationsRequestPlanCreateMutation,
  useProviderLocationsRequestPlanRetrieveQuery,
  useProviderLocationsRequestPlanUpdateMutation,
  useProviderLocationsRoutingOptionsQuery,
} from '@/services/api/generated/providerLocationRequestPlansService';

// All available scopes in display order
const ALL_SCOPES: TreatmentRecordTypes[] = [
  'Facility Records',
  'Facility Bills',
  'Radiology',
  'ER Physician Bills',
  'Other',
];

// Display labels for scopes
const SCOPE_LABELS: Record<TreatmentRecordTypes, string> = {
  'Facility Records': 'Facility Records',
  'Facility Bills': 'Facility Bills',
  Radiology: 'Radiology',
  'ER Physician Bills': 'ER Physician Bills',
  Other: 'Other',
  Bills: 'Bills',
  Records: 'Records',
  Unknown: 'Unknown',
};

type RoutingTarget =
  | { type: 'location'; id: string | null }
  | { type: 'service'; id: string };

interface ScopeConfig {
  enabled: boolean;
  routing: RoutingTarget;
}

type ScopeConfigMap = Record<TreatmentRecordTypes, ScopeConfig>;

const defaultRouting = (): RoutingTarget => ({
  type: 'location',
  id: null,
});

const initializeScopeConfig = (
  existingPlan: ProviderRequestPlanSchema | null | undefined,
): ScopeConfigMap => {
  // Initialize all scopes as disabled with default routing
  const config = ALL_SCOPES.reduce((acc, scope) => {
    acc[scope] = {
      enabled: false,
      routing: defaultRouting(),
    };
    return acc;
  }, {} as ScopeConfigMap);

  // Apply existing plan configuration
  if (existingPlan?.scopes) {
    existingPlan.scopes.forEach((scopeItem) => {
      if (scopeItem.scope in config) {
        config[scopeItem.scope] = {
          enabled: true,
          routing:
            scopeItem.routing.type === 'service'
              ? { type: 'service', id: scopeItem.routing.service_id! }
              : {
                  type: 'location',
                  id: scopeItem.routing.location_id ?? null,
                },
        };
      }
    });
  }

  return config;
};

interface RoutingSelectorProps {
  value: RoutingTarget;
  onChange: (value: RoutingTarget) => void;
  locations: ProviderRequestPlanRoutingOptionLocation[];
  services: ProviderRequestPlanRoutingOptionService[];
  disabled?: boolean;
}

const RoutingSelector = ({
  value,
  onChange,
  locations,
  services,
  disabled,
}: RoutingSelectorProps) => {
  const currentValue = useMemo(() => {
    if (value.type === 'location') {
      return `location:${value.id ?? 'null'}`;
    }
    return `service:${value.id}`;
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const [type, id] = e.target.value.split(':');
    if (type === 'location') {
      onChange({ type: 'location', id: id === 'null' ? null : id });
    } else {
      onChange({ type: 'service', id });
    }
  };

  return (
    <select
      className="h-9 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm text-zinc-900 disabled:bg-zinc-50 disabled:text-zinc-500"
      value={currentValue}
      onChange={handleChange}
      disabled={disabled}
    >
      {locations.length > 0 && (
        <optgroup label="Locations">
          {locations.map((loc) => (
            <option
              key={loc.id ?? 'this-location'}
              value={`location:${loc.id ?? 'null'}`}
            >
              {loc.is_anchor ? 'This location' : loc.name}
            </option>
          ))}
        </optgroup>
      )}
      {services.length > 0 && (
        <optgroup label="Services">
          {services.map((svc) => (
            <option key={svc.id} value={`service:${svc.id}`}>
              {svc.name}
              {svc.service_type ? ` (${svc.service_type})` : ''}
            </option>
          ))}
        </optgroup>
      )}
    </select>
  );
};

interface ScopeRowProps {
  scope: TreatmentRecordTypes;
  config: ScopeConfig;
  onToggle: (enabled: boolean) => void;
  onRoutingChange: (routing: RoutingTarget) => void;
  locations: ProviderRequestPlanRoutingOptionLocation[];
  services: ProviderRequestPlanRoutingOptionService[];
}

const ScopeRow = ({
  scope,
  config,
  onToggle,
  onRoutingChange,
  locations,
  services,
}: ScopeRowProps) => {
  return (
    <div className="flex items-center gap-4 py-2 border-b border-zinc-100 last:border-b-0">
      <CheckboxField className="flex-shrink-0">
        <Checkbox
          checked={config.enabled}
          onChange={(checked) => onToggle(checked)}
        />
      </CheckboxField>
      <div className="flex-1 min-w-0">
        <span
          className={`text-sm font-medium ${config.enabled ? 'text-zinc-900' : 'text-zinc-400'}`}
        >
          {SCOPE_LABELS[scope]}
        </span>
      </div>
      <div className="w-48 flex-shrink-0">
        <RoutingSelector
          value={config.routing}
          onChange={onRoutingChange}
          locations={locations}
          services={services}
          disabled={!config.enabled}
        />
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Standalone Content Component (can be embedded in other modals)
// ─────────────────────────────────────────────────────────────────────────────

export interface ConfigureRequestPlanContentProps {
  locationId: string;
  locationName: string;
  onSaved?: () => void;
  onCancel?: () => void;
  /** Hide the header (title + description). Useful when embedding in a dialog with its own title. */
  hideHeader?: boolean;
}

export const ConfigureRequestPlanContent = ({
  locationId,
  locationName,
  onSaved,
  onCancel,
  hideHeader,
}: ConfigureRequestPlanContentProps) => {
  // Fetch existing plan
  const planQuery = useProviderLocationsRequestPlanRetrieveQuery(
    { providerLocationId: locationId, includeInactive: true },
    { skip: !locationId },
  );

  // Fetch routing options
  const routingQuery = useProviderLocationsRoutingOptionsQuery(
    { providerLocationId: locationId },
    { skip: !locationId },
  );

  // Mutations
  const [createPlan, { isLoading: isCreating }] =
    useProviderLocationsRequestPlanCreateMutation();
  const [updatePlan, { isLoading: isUpdating }] =
    useProviderLocationsRequestPlanUpdateMutation();

  // Local state for scope configuration
  const [scopeConfig, setScopeConfig] = useState<ScopeConfigMap>(() =>
    initializeScopeConfig(null),
  );

  // Check for 404 (no plan exists)
  const planNotFound = (() => {
    const err = planQuery.error;
    if (!err || typeof err !== 'object') return false;
    if ('status' in err && err.status === 404) return true;
    if ('originalStatus' in err && err.originalStatus === 404) return true;
    return false;
  })();

  const existingPlan = planQuery.data;

  // Initialize config when plan data loads
  useEffect(() => {
    if (planQuery.isLoading) return;
    setScopeConfig(initializeScopeConfig(existingPlan));
  }, [planQuery.isLoading, existingPlan]);

  const locations = routingQuery.data?.locations ?? [];
  const services = routingQuery.data?.services ?? [];

  const handleToggleScope = (scope: TreatmentRecordTypes, enabled: boolean) => {
    setScopeConfig((prev) => ({
      ...prev,
      [scope]: {
        ...prev[scope],
        enabled,
      },
    }));
  };

  const handleRoutingChange = (
    scope: TreatmentRecordTypes,
    routing: RoutingTarget,
  ) => {
    setScopeConfig((prev) => ({
      ...prev,
      [scope]: {
        ...prev[scope],
        routing,
      },
    }));
  };

  const enabledScopeCount = useMemo(() => {
    return ALL_SCOPES.filter((s) => scopeConfig[s]?.enabled).length;
  }, [scopeConfig]);

  const handleSave = async () => {
    // Build scopes array from config
    const scopes: ProviderRequestPlanScopeSchema[] = ALL_SCOPES.filter(
      (s) => scopeConfig[s]?.enabled,
    ).map((scope, index) => {
      const config = scopeConfig[scope];
      return {
        scope,
        routing:
          config.routing.type === 'service'
            ? { type: 'service' as const, service_id: config.routing.id }
            : {
                type: 'location' as const,
                location_id: config.routing.id,
              },
        sort_order: index,
      };
    });

    try {
      if (existingPlan?.id) {
        await updatePlan({
          providerLocationId: locationId,
          planId: existingPlan.id as string,
          providerRequestPlanUpsertRequest: { scopes },
        }).unwrap();
      } else {
        await createPlan({
          providerLocationId: locationId,
          providerRequestPlanUpsertRequest: { scopes },
        }).unwrap();
      }

      toast.success('Request plan saved');
      onSaved?.();
    } catch {
      toast.error('Failed to save request plan');
    }
  };

  const isLoading = planQuery.isLoading || routingQuery.isLoading;
  const isSaving = isCreating || isUpdating;

  return (
    <div className="space-y-4">
      {!hideHeader && (
        <div>
          <h2 className="text-lg font-semibold text-zinc-900">
            Configure Request Plan
          </h2>
          <p className="text-sm text-zinc-500">{locationName}</p>
        </div>
      )}

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-zinc-400 animate-spin" />
          <p className="mt-3 text-sm text-zinc-500">Loading...</p>
        </div>
      ) : (
        <>
          <p className="text-sm text-zinc-600">
            Select which record types to request and where to route them:
          </p>

          <div className="rounded-lg border border-zinc-200 bg-white">
            {/* Header */}
            <div className="flex items-center gap-4 px-4 py-2 bg-zinc-50 border-b border-zinc-200">
              <div className="w-5 flex-shrink-0" />
              <div className="flex-1 text-xs font-semibold text-zinc-500 uppercase tracking-wide">
                Scope
              </div>
              <div className="w-48 flex-shrink-0 text-xs font-semibold text-zinc-500 uppercase tracking-wide">
                Route to
              </div>
            </div>

            {/* Rows */}
            <div className="px-4">
              {ALL_SCOPES.map((scope) => (
                <ScopeRow
                  key={scope}
                  scope={scope}
                  config={scopeConfig[scope]}
                  onToggle={(enabled) => handleToggleScope(scope, enabled)}
                  onRoutingChange={(routing) =>
                    handleRoutingChange(scope, routing)
                  }
                  locations={locations}
                  services={services}
                />
              ))}
            </div>
          </div>

          {enabledScopeCount > 0 && (
            <div className="flex items-center gap-2 text-sm text-emerald-700">
              <Check className="h-4 w-4" />
              {enabledScopeCount} scope{enabledScopeCount === 1 ? '' : 's'} will
              be configured
            </div>
          )}

          {planNotFound && (
            <p className="text-xs text-zinc-500">
              No existing plan. A new plan will be created.
            </p>
          )}
        </>
      )}

      <div className="flex justify-end gap-3 pt-4 border-t border-zinc-200">
        <Button outline onClick={onCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleSave}
          disabled={isSaving || isLoading}
        >
          {isSaving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Saving...
            </>
          ) : (
            'Save Plan'
          )}
        </Button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Modal Wrapper (for standalone use)
// ─────────────────────────────────────────────────────────────────────────────

export interface ConfigureRequestPlanModalProps {
  open: boolean;
  onClose: () => void;
  locationId: string;
  locationName: string;
  onSaved?: () => void;
  zIndex?: number;
}

export const ConfigureRequestPlanModal = ({
  open,
  onClose,
  locationId,
  locationName,
  onSaved,
  zIndex,
}: ConfigureRequestPlanModalProps) => {
  return (
    <Dialog open={open} onClose={onClose} size="2xl" zIndex={zIndex}>
      <DialogTitle className="sr-only">Configure Request Plan</DialogTitle>
      <DialogDescription className="sr-only">{locationName}</DialogDescription>
      <DialogBody>
        <ConfigureRequestPlanContent
          locationId={locationId}
          locationName={locationName}
          onSaved={() => {
            onSaved?.();
            onClose();
          }}
          onCancel={onClose}
        />
      </DialogBody>
      <DialogActions className="sr-only" />
    </Dialog>
  );
};
